<template>
  <avs-card class="pb-4">
    <template #title>
      <!-- ======= BARRE D'OUTILS (responsive, multi-lignes) ======= -->
      <v-container fluid class="py-3 filter-bar bgTable">
        <v-row dense align="center">
          <!-- Recherche -->
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-text-field
              dense
              outlined
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              :label="$t('commons.search')"
              v-model="search"
              @input="onSearchChange"
            />
          </v-col>

          <!-- Filtre de statut -->
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
              dense
              outlined
              hide-details
              :items="statusItems"
              v-model="statusFilter"
              :label="$t('Statut')"
              prepend-inner-icon="mdi-filter"
              clearable
              @change="onFilterChange"
            />
          </v-col>

          <!-- Filtre type utilisateur -->
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
              dense
              outlined
              hide-details
              :items="userTypeItems"
              v-model="userTypeFilter"
              :label="$t('Type utilisateur')"
              prepend-inner-icon="mdi-account-outline"
              clearable
              @change="onFilterChange"
            />
          </v-col>

          <!-- Filtre type vehicule -->
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
              dense
              outlined
              hide-details
              :items="vehicleTypeItems"
              v-model="vehicleTypeFilter"
              :label="$t('Type vehicule')"
              prepend-inner-icon="mdi-car-side"
              clearable
              @change="onFilterChange"
            />
          </v-col>

          <!-- Filtre station -->
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-autocomplete
              dense
              outlined
              hide-details
              :items="stations"
              item-text="name"
              item-value="id"
              v-model="stationFilter"
              :label="$t('Station')"
              prepend-inner-icon="mdi-gas-station"
              clearable
              @change="onFilterChange"
            />
          </v-col>

          <!-- Date début -->
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-menu
              v-model="startMenu"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-on="on"
                  dense
                  outlined
                  hide-details
                  readonly
                  clearable
                  prepend-inner-icon="mdi-calendar-start"
                  :label="$t('Date début')"
                  :value="startDate"
                  @click:clear="startDate = null; onFilterChange()"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                @input="startMenu = false; onFilterChange()"
              />
            </v-menu>
          </v-col>

          <!-- Date fin -->
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-menu
              v-model="endMenu"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-on="on"
                  dense
                  outlined
                  hide-details
                  readonly
                  clearable
                  prepend-inner-icon="mdi-calendar-end"
                  :label="$t('Date fin')"
                  :value="endDate"
                  @click:clear="endDate = null; onFilterChange()"
                />
              </template>
              <v-date-picker
                v-model="endDate"
                @input="endMenu = false; onFilterChange()"
              />
            </v-menu>
          </v-col>

          <!-- Actions -->
          <v-col cols="12" lg="6" class="d-flex flex-wrap align-center" :class="$vuetify.breakpoint.lgAndUp ? 'justify-end' : 'justify-start'">
            <v-btn
              small
              outlined
              color="success"
              class="mr-2 mb-2 mb-lg-0"
              @click="exportFidelityUsers"
              :loading="exportLoading"
            >
              <v-icon left>mdi-microsoft-excel</v-icon>{{ $t('Utilisateurs') }}
            </v-btn>
            <v-btn
              small
              outlined
              color="secondary"
              class="mr-2 mb-2 mb-lg-0"
              @click="exportVehicleValidations"
              :loading="exportValidationsLoading"
            >
              <v-icon left>mdi-car-cog</v-icon>{{ $t('Validations') }}
            </v-btn>
            <v-btn
              small
              outlined
              color="primary"
              class="mb-2 mb-lg-0"
              @click="resetAndReload"
              :loading="loading && currentPage === 1"
            >
              <v-icon left>mdi-refresh</v-icon>{{ $t('Actualiser') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </template>
    
    <!-- ======= TABLE ======= -->
    <template #content>
      <!-- ======= KPIs Section (sous les filtres) ======= -->
      <v-row class="stats-section ma-4" v-if="kpiStats">
        <v-col cols="12">
          <div class="stats-container">
            <!-- Total demandes de véhicules -->
            <v-card class="stat-card stat-card-main" elevation="4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label-mobile">{{ $t('Total demandes') }}</div>
                  <div class="stat-value-mobile">
                    {{ formatNumber(kpiStats.total_vehicles) }}
                  </div>
                </div>
                <div class="stat-icon-mobile">
                  <v-icon size="40" color="white">mdi-car-multiple</v-icon>
                </div>
              </div>
              <v-divider class="my-0" style="border-color: rgba(255,255,255,0.3);" />
              <div class="stat-secondary mt-2">
                <v-icon small color="white">mdi-account-group</v-icon>
                {{ formatNumber(kpiStats.total_users) }} {{ $t('utilisateurs') }}
              </div>
            </v-card>

            <!-- En attente -->
            <v-card class="stat-card stat-card-product bgTable" elevation="3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label-mobile">{{ $t('En attente') }}</div>
                  <div class="stat-value-mobile stat-value-warning">
                    {{ formatNumber(kpiStats.pending) }}
                  </div>
                </div>
                <div class="stat-icon-mobile stat-icon-warning">
                  <v-icon size="40" color="orange">mdi-clock-alert</v-icon>
                </div>
              </div>
            </v-card>

            <!-- Validés -->
            <v-card class="stat-card stat-card-product bgTable" elevation="3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label-mobile">{{ $t('Validés') }}</div>
                  <div class="stat-value-mobile stat-value-success">
                    {{ formatNumber(kpiStats.approved) }}
                  </div>
                </div>
                <div class="stat-icon-mobile stat-icon-success">
                  <v-icon size="40" color="success">mdi-check-decagram</v-icon>
                </div>
              </div>
            </v-card>

            <!-- Refusés -->
            <v-card class="stat-card stat-card-product bgTable" elevation="3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label-mobile">{{ $t('Refusés') }}</div>
                  <div class="stat-value-mobile stat-value-error">
                    {{ formatNumber(kpiStats.rejected) }}
                  </div>
                </div>
                <div class="stat-icon-mobile stat-icon-error">
                  <v-icon size="40" color="error">mdi-close-circle</v-icon>
                </div>
              </div>
            </v-card>
          </div>
        </v-col>
      </v-row>

      <v-data-table
        class="ma-6 bgTable elevation-0"
        dense
        :headers="headers"
        :items="items"
        :loading="loading && currentPage === 1"
        hide-default-footer
        item-key="id"
        :no-data-text="$t('messages.information.no_data')"
        disable-pagination
        :items-per-page="-1"
      >
        <!-- UTILISATEUR -->
        <template v-slot:item.user="{ item }">
          <div class="d-flex align-center py-2">
            <v-avatar size="40" color="primary" class="mr-3 elevation-2">
              <span class="white--text text-subtitle-2 font-weight-bold">
                {{ getUserInitials(item) }}
              </span>
            </v-avatar>
            <div>
              <div class="text-no-wrap font-weight-bold capitalize">
                {{ userFullname(item) }}
              </div>
              <div class="text-caption grey--text">
                <v-icon x-small class="mr-1">mdi-phone</v-icon>
                {{ userPhone(item) }}
              </div>
            </div>
          </div>
        </template>
        
        <!-- TYPE UTILISATEUR -->
        <template v-slot:item.user_type="{ item }">
          <v-chip x-small :color="item.user_is_mobile ? 'blue lighten-4' : 'orange lighten-4'" :text-color="item.user_is_mobile ? 'blue darken-3' : 'orange darken-3'">
            <v-icon x-small left>{{ item.user_is_mobile ? 'mdi-cellphone' : 'mdi-cellphone-off' }}</v-icon>
            {{ item.user_is_mobile ? 'Mobile' : 'Sans smartphone' }}
          </v-chip>
        </template>

        <!-- STATION D'ORIGINE -->
        <template v-slot:item.origin_station="{ item }">
          {{ item.origin_station || '-' }}
        </template>

        <!-- IMMATRICULATION -->
        <template v-slot:item.plate_number="{ item }">
          <div class="plate-number">
            <v-icon small class="mr-2" color="primary">mdi-card-account-details</v-icon>
            <span class="font-weight-bold">{{ item.plate_number }}</span>
          </div>
          <div class="text-caption grey--text">
            <v-icon x-small class="mr-1">mdi-calendar</v-icon>
            {{ formatDate(userJoinedAt(item)) }}
          </div>
        </template>
        
        <!-- TYPE avec icône -->
        <template v-slot:item.vehicle_type="{ item }">
          <v-chip small :color="getVehicleTypeColor(item.vehicle_type)" class="font-weight-medium" text-color="white">
            <v-icon left x-small>{{ getVehicleTypeIcon(item.vehicle_type) }}</v-icon>
            {{ vehicleTypeLabel(item.vehicle_type) }}
          </v-chip>
        </template>
        
        <!-- STATUT TRI-ÉTAT amélioré -->
        <template v-slot:item.decision_status="{ item }">
          <v-chip 
            small 
            :color="statusColor(item.decision_status)" 
            text-color="white"
            class="font-weight-medium"
          >
            <v-icon left x-small>{{ statusIcon(item.decision_status) }}</v-icon>
            {{ statusLabel(item.decision_status) }}
          </v-chip>
        </template>
        
        <!-- PAR DÉFAUT -->
        <template v-slot:item.is_default="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon 
                v-bind="attrs"
                v-on="on"
                :color="item.is_default ? 'amber darken-2' : 'grey lighten-1'"
                size="24"
              >
                {{ item.is_default ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
            </template>
            <span>{{ item.is_default ? $t('Véhicule par défaut') : $t('Non défini par défaut') }}</span>
          </v-tooltip>
        </template>
        
        <!-- DATE -->
        <template v-slot:item.created_at="{ item }">
          <div class="text-no-wrap">
            <div class="text-body-2">{{ formatDate(item.created_at) }}</div>
            <div class="text-caption grey--text">{{ formatTime(item.created_at) }}</div>
          </div>
        </template>
        
        <!-- ACTIONS améliorées -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center action-buttons">
            <!-- Voir -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  color="secondary"
                  :iconI="'mdi-eye'"
                  :spantext="$t('Voir les détails')"
                  label
                  fab
                  @click.stop="openDialog(item)"
                />
              </template>
              <span>{{ $t('Voir les détails') }}</span>
            </v-tooltip>
            
            <!-- Statuer -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  color="primary"
                  :iconI="'mdi-gavel'"
                  :spantext="$t('Valider/Refuser')"
                  label
                  fab
                  @click.stop="openDecision(item)"
                />
              </template>
            </v-tooltip>
            
            <!-- Changer type -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  color="purple"
                  :iconI="'mdi-car-cog'"
                  :spantext="$t('Changer le type')"
                  label
                  fab
                  @click.stop="openChangeType(item)"
                />
              </template>
            </v-tooltip>
            
            <!-- Définir par défaut -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-star'"
                  :spantext="item.is_default ? $t('Déjà par défaut') : $t('Définir par défaut')"
                  label
                  fab
                  small
                  :color="item.is_default ? 'amber darken-2' : 'grey'"
                  @click.stop="askConfirm('default', item)"
                  :disabled="item.is_default"
                />
              </template>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
      
      <!-- Loader pour le chargement de plus d'éléments -->
      <div 
        ref="loadMoreTrigger" 
        class="text-center py-4 ma-6"
        v-if="hasMore"
        style="min-height: 80px;"
      >
        <v-progress-circular
          v-if="loadingMore"
          indeterminate
          color="primary"
          size="40"
        ></v-progress-circular>
        <div v-if="loadingMore" class="mt-2 text-body-2 grey--text">
          {{ $t('Chargement de plus de véhicules...') }}
        </div>
      </div>
      
      <!-- Fin de la liste -->
      <div v-if="!hasMore && items.length > 0" class="text-center py-4 ma-6">
        <v-icon color="success" size="32">mdi-check-circle-outline</v-icon>
        <div class="text-body-1 font-weight-medium mt-2">{{ $t('Tous les véhicules sont affichés') }}</div>
        <div class="text-caption grey--text">{{ items.length }} {{ $t('véhicule(s) au total') }}</div>
      </div>
      
      <!-- ======= DIALOG DÉTAIL ======= -->
      <v-dialog v-model="dialog.open" width="800px">
        <VehicleReviewDialog v-model="dialog.item" @close="dialog.open=false" />
      </v-dialog>
      
      <!-- ======= DIALOG CHANGER TYPE ======= -->
      <v-dialog v-model="changeType.open" max-width="600">
        <v-card class="rounded-xl">
          <v-sheet color="purple" dark class="pa-4 d-flex align-center">
            <v-icon x-large class="mr-3">mdi-car-cog</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">{{ $t('Changer le type de véhicule') }}</div>
              <div class="opacity-80">{{ changeType.item?.plate_number }}</div>
            </div>
          </v-sheet>
          
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12">
                <div class="text-subtitle-2 mb-2">{{ $t('Utilisateur') }}</div>
                <v-chip label class="mb-4">
                  <v-icon left small>mdi-account</v-icon>
                  <span class="capitalize">{{ userFullname(changeType.item) }}</span>
                </v-chip>
              </v-col>
              
              <v-col cols="12">
                <div class="text-subtitle-2 mb-2">{{ $t('Type actuel') }}</div>
                <v-chip :color="getVehicleTypeColor(changeType.item?.vehicle_type)" text-color="white" class="mb-4">
                  <v-icon left small>{{ getVehicleTypeIcon(changeType.item?.vehicle_type) }}</v-icon>
                  {{ vehicleTypeLabel(changeType.item?.vehicle_type) }}
                </v-chip>
              </v-col>
              
              <v-col cols="12">
                <v-select
                  v-model="changeType.newType"
                  :items="vehicleTypeOptions"
                  item-text="label"
                  item-value="value"
                  :label="$t('Nouveau type de véhicule')"
                  outlined
                  dense
                  prepend-inner-icon="mdi-car"
                  :rules="[v => !!v || $t('Champ obligatoire')]"
                >
                  <template v-slot:item="{ item }">
                    <v-icon left small :color="getVehicleTypeColor(item.value)">
                      {{ getVehicleTypeIcon(item.value) }}
                    </v-icon>
                    {{ item.label }}
                  </template>
                  <template v-slot:selection="{ item }">
                    <v-icon left small :color="getVehicleTypeColor(item.value)">
                      {{ getVehicleTypeIcon(item.value) }}
                    </v-icon>
                    {{ item.label }}
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-card-text>
          
          <v-divider />
          <v-card-actions class="pa-4 d-flex justify-end">
            <v-btn text @click="changeType.open = false">
              <v-icon left>mdi-close</v-icon>{{ $t('commons.cancel') }}
            </v-btn>
            <v-btn
              color="purple"
              dark
              :loading="changeType.loading"
              @click="doChangeType"
              :disabled="!changeType.newType || changeType.newType === changeType.item?.vehicle_type"
            >
              <v-icon left>mdi-check</v-icon>{{ $t('Modifier') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <!-- ======= DIALOG STATUER (Valider/Refuser) ======= -->
      <v-dialog v-model="decision.open" max-width="800">
        <v-card class="rounded-xl">
          <!-- HÉRO -->
          <v-sheet color="primary" dark class="pa-4 d-flex align-center">
            <v-icon x-large class="mr-3">mdi-gavel</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">{{ $t('Statuer sur le véhicule') }}</div>
              <div class="opacity-80">
                {{ decision.item?.plate_number }} · {{ vehicleTypeLabel(decision.item?.vehicle_type) }}
              </div>
            </div>
          </v-sheet>
          
          <v-card-text class="pt-6 bgTable">
            <v-row>
              <!-- Colonne Utilisateur -->
              <v-col cols="12" md="6" class="bgTable">
                <v-list dense two-line class="bgTable">
                  <v-subheader>{{ $t('Utilisateur') }}</v-subheader>
                  <v-list-item>
                    <v-list-item-avatar color="primary">
                      <span class="white--text font-weight-bold">{{ getUserInitials(decision.item) }}</span>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="capitalize">{{ userFullname(decision.item) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ userPhone(decision.item) }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-calendar-clock</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ formatDate(userJoinedAt(decision.item)) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Date d\'inscription') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              
              <!-- Colonne Véhicule -->
              <v-col cols="12" md="6" class="bgTable">
                <v-list dense two-line class="bgTable">
                  <v-subheader>{{ $t('Véhicule') }}</v-subheader>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-car-info</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold d-flex align-center">
                        {{ decision.item?.plate_number }}
                        
                        <!-- Badge de vérification -->
                        <v-chip 
                          v-if="plateVerificationStatus === 'exists'"
                          x-small 
                          class="ml-2"
                          color="error"
                          text-color="white"
                          @click="showPlateDetails"
                          style="cursor: pointer;"
                        >
                          <v-icon x-small left>mdi-alert-circle</v-icon>
                          Existe B2B
                        </v-chip>
                        
                        <v-chip 
                          v-if="plateVerificationStatus === 'clear'"
                          x-small 
                          class="ml-2"
                          color="success"
                          text-color="white"
                        >
                          <v-icon x-small left>mdi-check-circle</v-icon>
                          Disponible
                        </v-chip>
                        
                        <!-- Loader -->
                        <v-progress-circular
                          v-if="plateCheckLoading"
                          size="16"
                          width="2"
                          indeterminate
                          color="primary"
                          class="ml-2"
                        />
                      </v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Immatriculation') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon>{{ getVehicleTypeIcon(decision.item?.vehicle_type) }}</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ vehicleTypeLabel(decision.item?.vehicle_type) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Type') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-check-circle</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-chip small :color="statusColor(decision.item?.decision_status)" text-color="white">
                          <v-icon left x-small>{{ statusIcon(decision.item?.decision_status) }}</v-icon>
                          {{ statusLabel(decision.item?.decision_status) }}
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Statut actuel') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
            
            <!-- Vignettes pièces -->
            <v-row class="mt-2">
              <v-col cols="12" md="6" v-if="decision.item?.carte_grise">
                <div class="text-subtitle-2 mb-2">{{ $t('Carte grise') }}</div>
                <v-img 
                  :src="decision.item.carte_grise" 
                  contain 
                  height="160" 
                  class="rounded-lg elevation-2 grey lighten-4" 
                />
              </v-col>
              <v-col cols="12" md="6" v-if="decision.item?.driving_license">
                <div class="text-subtitle-2 mb-2">{{ $t('Permis') }}</div>
                <v-img 
                  :src="decision.item.driving_license" 
                  contain 
                  height="160" 
                  class="rounded-lg elevation-2 grey lighten-4" 
                />
              </v-col>
            </v-row>
          </v-card-text>
          
          <v-divider />
          <v-card-actions class="pa-4 d-flex justify-end">
            <v-btn text @click="decision.open = false">
              <v-icon left>mdi-close</v-icon>{{ $t('commons.cancel') }}
            </v-btn>
            <v-btn color="error" dark :loading="decision.loadingReject" @click="onReject">
              <v-icon left>mdi-close-circle</v-icon>{{ $t('Refuser') }}
            </v-btn>
            <v-btn color="success" dark :loading="decision.loadingApprove" @click="onApprove">
              <v-icon left>mdi-check-decagram</v-icon>{{ $t('Valider') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <!-- ======= DIALOG CONFIRMATION (Définir par défaut) ======= -->
      <v-dialog v-model="confirm.open" max-width="680">
        <v-card v-if="confirm.open" class="rounded-xl">
          <v-sheet :color="confirmMeta.color" dark class="pa-4 d-flex align-center">
            <v-icon x-large class="mr-3">{{ confirmMeta.icon }}</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">{{ confirmMeta.title }}</div>
              <div class="opacity-80">{{ confirmMeta.subtitle }}</div>
            </div>
          </v-sheet>
          
          <v-card-text class="pt-6 bgTable">
            <v-row>
              <v-col cols="12" md="6" class="bgTable">
                <v-list dense two-line class="bgTable">
                  <v-subheader>{{ $t('Utilisateur') }}</v-subheader>
                  <v-list-item>
                    <v-list-item-avatar color="primary">
                      <span class="white--text font-weight-bold">{{ getUserInitials(confirm.item) }}</span>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="capitalize">{{ userFullname(confirm.item) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ userPhone(confirm.item) }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              <v-col cols="12" md="6" class="bgTable">
                <v-list dense two-line class="bgTable">
                  <v-subheader>{{ $t('Véhicule') }}</v-subheader>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-car-info</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">{{ confirm.item?.plate_number }}</v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Immatriculation') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>
          
          <v-divider />
          <v-card-actions class="pa-4 d-flex justify-end">
            <v-btn text @click="confirm.open = false">
              <v-icon left>mdi-close</v-icon>{{ $t('commons.cancel') }}
            </v-btn>
            <v-btn
              :color="confirmMeta.color"
              dark
              :loading="confirm.loading"
              @click="doConfirm"
            >
              <v-icon left>{{ confirmMeta.icon }}</v-icon>{{ confirmMeta.cta }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <!-- ======= MODALE D'ALERTE PLAQUE EXISTANTE COMPLÈTE ======= -->
      <v-dialog v-model="plateWarning.open" max-width="900" persistent>
        <v-card class="rounded-xl">
          <v-sheet color="error" dark class="pa-4 d-flex align-center">
            <v-icon x-large class="mr-3">mdi-alert-circle</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">
                {{ $t('Plaque d\'immatriculation déjà enregistrée') }}
              </div>
              <div class="opacity-80">
                {{ plateWarning.data?.source === 'B2B' 
                  ? $t('Cette plaque existe déjà dans le système B2B professionnel') 
                  : $t('Cette plaque est déjà enregistrée par un autre utilisateur')
                }}
              </div>
            </div>
          </v-sheet>
          
          <v-card-text class="pt-6">
            <!-- Comparaison des plaques -->
            <v-alert type="warning" outlined class="mb-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption grey--text mb-1">{{ $t('Plaque saisie:') }}</div>
                  <div class="text-h6 font-weight-bold error--text">
                    <v-icon left color="error">mdi-car-info</v-icon>
                    {{ plateWarning.inputPlate }}
                  </div>
                </div>
                
                <v-icon large>mdi-arrow-right</v-icon>
                
                <div>
                  <div class="text-caption grey--text mb-1">{{ $t('Déjà enregistrée comme:') }}</div>
                  <div class="text-h6 font-weight-bold primary--text">
                    <v-icon left color="primary">mdi-database-check</v-icon>
                    {{ plateWarning.data?.plate_original }}
                  </div>
                </div>
              </div>
              
              <v-divider class="my-3" />
              
              <div class="text-caption">
                <v-icon small left>mdi-information</v-icon>
                {{ $t('Les plaques sont identiques après normalisation (sans espaces, tirets, casse)') }}
              </div>
            </v-alert>
            
            <!-- ========== INFORMATIONS VÉHICULE B2B ========== -->
            <v-row v-if="plateWarning.data?.source === 'B2B' && plateWarning.data?.vehicle">
              <v-col cols="12">
                <v-card outlined class="mb-4">
                  <v-card-title class="error white--text py-2">
                    <v-icon left color="white">mdi-shield-alert</v-icon>
                    {{ $t('Véhicule Professionnel (B2B)') }}
                  </v-card-title>
                  
                  <v-card-text class="pt-4">
                    <v-row>
                      <!-- Colonne 1: Infos entreprise -->
                      <v-col cols="12" md="6">
                        <v-list dense two-line class="bgTable">
                          <v-subheader class="font-weight-bold primary--text">
                            <v-icon left small color="primary">mdi-office-building</v-icon>
                            {{ $t('Entreprise') }}
                          </v-subheader>
                          
                          <v-list-item v-if="plateWarning.data.vehicle.company">
                            <v-list-item-avatar v-if="plateWarning.data.vehicle.company.logo">
                              <v-img :src="plateWarning.data.vehicle.company.logo" />
                            </v-list-item-avatar>
                            <v-list-item-avatar v-else color="primary">
                              <v-icon color="white">mdi-domain</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">
                                {{ plateWarning.data.vehicle.company.name }}
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                <v-chip 
                                  x-small 
                                  :color="plateWarning.data.vehicle.company.company_type === 'Business' ? 'blue' : 'purple'" 
                                  text-color="white"
                                >
                                  {{ plateWarning.data.vehicle.company.company_type }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          
                          <v-list-item v-if="plateWarning.data.vehicle.department">
                            <v-list-item-icon><v-icon>mdi-sitemap</v-icon></v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{ plateWarning.data.vehicle.department.name }}</v-list-item-title>
                              <v-list-item-subtitle>{{ $t('Département') }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                      
                      <!-- Colonne 2: Infos véhicule -->
                      <v-col cols="12" md="6">
                        <v-list dense two-line class="bgTable">
                          <v-subheader class="font-weight-bold primary--text">
                            <v-icon left small color="primary">mdi-car</v-icon>
                            {{ $t('Véhicule') }}
                          </v-subheader>
                          
                          <v-list-item>
                            <v-list-item-icon><v-icon>mdi-card-account-details</v-icon></v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">
                                {{ plateWarning.data.vehicle.registration_number }}
                              </v-list-item-title>
                              <v-list-item-subtitle>{{ $t('Immatriculation') }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          
                          <v-list-item v-if="plateWarning.data.vehicle.make">
                            <v-list-item-icon><v-icon>mdi-car-info</v-icon></v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ plateWarning.data.vehicle.make }} {{ plateWarning.data.vehicle.model }}
                                <span v-if="plateWarning.data.vehicle.year" class="grey--text">
                                  ({{ plateWarning.data.vehicle.year }})
                                </span>
                              </v-list-item-title>
                              <v-list-item-subtitle>{{ $t('Marque et modèle') }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          
                          <v-list-item v-if="plateWarning.data.vehicle.vehicle_type">
                            <v-list-item-icon>
                              <v-icon>{{ getVehicleTypeIcon(plateWarning.data.vehicle.vehicle_type) }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>
                                <v-chip 
                                  small 
                                  :color="getVehicleTypeColor(plateWarning.data.vehicle.vehicle_type)"
                                  text-color="white"
                                >
                                  {{ vehicleTypeLabel(plateWarning.data.vehicle.vehicle_type) }}
                                </v-chip>
                              </v-list-item-title>
                              <v-list-item-subtitle>{{ $t('Type') }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          
                          <v-list-item v-if="plateWarning.data.vehicle.color">
                            <v-list-item-icon><v-icon>mdi-palette</v-icon></v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{ plateWarning.data.vehicle.color }}</v-list-item-title>
                              <v-list-item-subtitle>{{ $t('Couleur') }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                    </v-row>
                    
                    <!-- Infos conducteur/utilisateur -->
                    <v-row v-if="plateWarning.data.vehicle.driver">
                      <v-col cols="12">
                        <v-divider class="my-2" />
                        <v-list dense two-line class="bgTable">
                          <v-subheader class="font-weight-bold primary--text">
                            <v-icon left small color="primary">mdi-account</v-icon>
                            {{ $t('Conducteur / Utilisateur') }}
                          </v-subheader>
                          
                          <v-list-item>
                            <v-list-item-avatar color="primary">
                              <span class="white--text font-weight-bold">
                                {{ getInitials(plateWarning.data.vehicle.driver.fullname) }}
                              </span>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold capitalize">
                                {{ plateWarning.data.vehicle.driver.fullname }}
                              </v-list-item-title>
                              <v-list-item-subtitle v-if="plateWarning.data.vehicle.driver.phone_number">
                                <v-icon x-small class="mr-1">mdi-phone</v-icon>
                                {{ plateWarning.data.vehicle.driver.phone_number }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                    </v-row>
                    
                    <!-- Infos produit -->
                    <v-row v-if="plateWarning.data.vehicle.product">
                      <v-col cols="12">
                        <v-divider class="my-2" />
                        <v-list dense two-line class="bgTable">
                          <v-subheader class="font-weight-bold primary--text">
                            <v-icon left small color="primary">mdi-gas-station</v-icon>
                            {{ $t('Produit attribué') }}
                          </v-subheader>
                          
                          <v-list-item>
                            <v-list-item-icon><v-icon>mdi-fuel</v-icon></v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{ plateWarning.data.vehicle.product.name }}</v-list-item-title>
                              <v-list-item-subtitle>
                                {{ plateWarning.data.vehicle.product.price }} FCFA/L
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                    </v-row>
                    
                    <!-- Statut du véhicule -->
                    <v-row>
                      <v-col cols="12">
                        <v-divider class="my-2" />
                        <v-chip-group column>
                          <v-chip 
                            small 
                            :color="plateWarning.data.vehicle.is_activated ? 'success' : 'grey'"
                            text-color="white"
                          >
                            <v-icon left x-small>
                              {{ plateWarning.data.vehicle.is_activated ? 'mdi-check-circle' : 'mdi-close-circle' }}
                            </v-icon>
                            {{ plateWarning.data.vehicle.is_activated ? $t('Activé') : $t('Désactivé') }}
                          </v-chip>
                          
                          <v-chip 
                            v-if="plateWarning.data.vehicle.is_blocked"
                            small 
                            color="error"
                            text-color="white"
                          >
                            <v-icon left x-small>mdi-block-helper</v-icon>
                            {{ $t('Bloqué') }}
                          </v-chip>
                          
                          <v-chip 
                            v-if="plateWarning.data.vehicle.created_at"
                            small 
                            outlined
                          >
                            <v-icon left x-small>mdi-calendar</v-icon>
                            {{ $t('Créé le') }} {{ formatDate(plateWarning.data.vehicle.created_at) }}
                          </v-chip>
                        </v-chip-group>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            
            <!-- ========== INFORMATIONS VÉHICULE USER (autre utilisateur) ========== -->
            <v-row v-if="plateWarning.data?.source === 'USER_VEHICLE' && plateWarning.data?.vehicle">
              <v-col cols="12">
                <v-card outlined class="mb-4">
                  <v-card-title class="warning white--text py-2">
                    <v-icon left color="white">mdi-account-alert</v-icon>
                    {{ $t('Véhicule d\'un autre utilisateur') }}
                  </v-card-title>
                  
                  <v-card-text class="pt-4">
                    <v-row>
                      <!-- Propriétaire -->
                      <v-col cols="12" md="6">
                        <v-list dense two-line class="bgTable">
                          <v-subheader class="font-weight-bold warning--text">
                            <v-icon left small color="warning">mdi-account</v-icon>
                            {{ $t('Propriétaire') }}
                          </v-subheader>
                          
                          <v-list-item v-if="plateWarning.data.vehicle.owner">
                            <v-list-item-avatar color="warning">
                              <span class="white--text font-weight-bold">
                                {{ getInitials(plateWarning.data.vehicle.owner.fullname) }}
                              </span>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold capitalize">
                                {{ plateWarning.data.vehicle.owner.fullname }}
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                <v-icon x-small class="mr-1">mdi-phone</v-icon>
                                {{ plateWarning.data.vehicle.owner.phone_number }}
                              </v-list-item-subtitle>
                              <v-list-item-subtitle v-if="plateWarning.data.vehicle.owner.email">
                                <v-icon x-small class="mr-1">mdi-email</v-icon>
                                {{ plateWarning.data.vehicle.owner.email }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                      
                      <!-- Véhicule -->
                      <v-col cols="12" md="6">
                        <v-list dense two-line class="bgTable">
                          <v-subheader class="font-weight-bold warning--text">
                            <v-icon left small color="warning">mdi-car</v-icon>
                            {{ $t('Véhicule') }}
                          </v-subheader>
                          
                          <v-list-item>
                            <v-list-item-icon><v-icon>mdi-card-account-details</v-icon></v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">
                                {{ plateWarning.data.vehicle.plate_number }}
                              </v-list-item-title>
                              <v-list-item-subtitle>{{ $t('Immatriculation') }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon>{{ getVehicleTypeIcon(plateWarning.data.vehicle.vehicle_type) }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>
                                <v-chip 
                                  small 
                                  :color="getVehicleTypeColor(plateWarning.data.vehicle.vehicle_type)"
                                  text-color="white"
                                >
                                  {{ vehicleTypeLabel(plateWarning.data.vehicle.vehicle_type) }}
                                </v-chip>
                              </v-list-item-title>
                              <v-list-item-subtitle>{{ $t('Type') }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          
                          <v-list-item>
                            <v-list-item-icon><v-icon>mdi-calendar</v-icon></v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{ formatDate(plateWarning.data.vehicle.created_at) }}</v-list-item-title>
                              <v-list-item-subtitle>{{ $t('Date d\'ajout') }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                    </v-row>
                    
                    <!-- Pièces justificatives -->
                    <v-row class="mt-2" v-if="plateWarning.data.vehicle.carte_grise || plateWarning.data.vehicle.driving_license">
                      <v-col cols="12" md="6" v-if="plateWarning.data.vehicle.carte_grise">
                        <div class="text-subtitle-2 mb-2">{{ $t('Carte grise') }}</div>
                        <v-img 
                          :src="plateWarning.data.vehicle.carte_grise" 
                          contain 
                          height="160" 
                          class="rounded-lg elevation-2 grey lighten-4" 
                        />
                      </v-col>
                      <v-col cols="12" md="6" v-if="plateWarning.data.vehicle.driving_license">
                        <div class="text-subtitle-2 mb-2">{{ $t('Permis') }}</div>
                        <v-img 
                          :src="plateWarning.data.vehicle.driving_license" 
                          contain 
                          height="160" 
                          class="rounded-lg elevation-2 grey lighten-4" 
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            
            <!-- Message d'avertissement final -->
            <v-alert type="error" text class="mt-4">
              <div class="d-flex align-start">
                <div>
                  <div class="font-weight-bold mb-2">{{ $t('Action impossible') }}</div>
                  <div>
                    {{ plateWarning.data?.source === 'B2B'
                      ? $t('Cette plaque d\'immatriculation est déjà enregistrée dans le système B2B professionnel. Un véhicule ne peut pas être enregistré deux fois dans le système. Veuillez contacter l\'administrateur de l\'entreprise concernée ou vérifier que la plaque saisie est correcte.')
                      : $t('Cette plaque d\'immatriculation est déjà utilisée par un autre utilisateur du système. Veuillez vérifier que la plaque saisie est correcte ou contacter le support.')
                    }}
                  </div>
                </div>
              </div>
            </v-alert>
          </v-card-text>
          
          <v-divider />
          <v-card-actions class="pa-4 d-flex justify-end">
            <v-btn color="primary" @click="closePlateWarning">
              <v-icon left>mdi-close</v-icon>{{ $t('Fermer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import VehicleAdminService from "@/services/VehicleAdminService";
import ShopDetailService from "@/services/ShopDetailService";
import VehicleReviewDialog from "@/components/activity/VehicleReviewDialog.vue";
import { toDatetime } from "@/utils/datetime";

const VEHICLE_LABELS = {
  VTC: "VTC",
  TAXI_COMPTEUR: "TAXI COMPTEUR",
  TAXI_COMMUNAL: "TAXI COMMUNAL",
  MINI_CAR_URBAIN: "MINI CAR URBAIN",
  MINI_CAR_INTER_URBAIN: "MINI CAR INTER URBAIN",
  AUTO_CAR: "AUTO CAR",
  POIDS_LOURDS: "POIDS LOURDS",
  CAMION: "CAMION",
  CAMIONNETTE: "CAMIONNETTE",
  REVENDEUR: "REVENDEUR",
  APPROVISIONNEMENT: "APPROVISIONNEMENT",
  APPROVISOINNEMENT: "APPROVISIONNEMENT",
  MOTO: "MOTO",
  TRICYCLE: "TRICYCLE",
  PARTICULIER: "PARTICULIER",
  AUTRES: "AUTRES",
  FLOTTE_VTC: "FLOTTE VTC",
};

export default {
  name: "VehiclesAdmin",
  components: { VehicleReviewDialog },
  
  data() {
    return {
      // États de chargement
      loading: false,
      loadingMore: false,
      exportLoading: false,
      plateCheckLoading: false,
      
      // KPIs
      kpiStats: null,

      // Données
      items: [],
      currentPage: 1,
      hasMore: true,
      totalCount: 0,
      
      // Filtres
      search: "",
      statusFilter: "pending",
      statusItems: [
        { text: this.$t('À statuer'), value: 'pending' },
        { text: this.$t('Validés'),   value: 'approved' },
        { text: this.$t('Refusés'),   value: 'rejected' },
      ],
      startDate: null,
      endDate: null,
      startMenu: false,
      endMenu: false,
      userTypeFilter: null,
      userTypeItems: [
        { text: this.$t('Mobile'), value: 'mobile' },
        { text: this.$t('Sans smartphone'), value: 'no_app' },
      ],
      stationFilter: null,
      stations: [],
      vehicleTypeFilter: null,
      vehicleTypeItems: [
        { text: 'VTC', value: 'VTC' },
        { text: 'TAXI COMPTEUR', value: 'TAXI_COMPTEUR' },
        { text: 'TAXI COMMUNAL', value: 'TAXI_COMMUNAL' },
        { text: 'MINI CAR URBAIN', value: 'MINI_CAR_URBAIN' },
        { text: 'MINI CAR INTER URBAIN', value: 'MINI_CAR_INTER_URBAIN' },
        { text: 'AUTO CAR', value: 'AUTO_CAR' },
        { text: 'POIDS LOURDS', value: 'POIDS_LOURDS' },
        { text: 'CAMION', value: 'CAMION' },
        { text: 'CAMIONNETTE', value: 'CAMIONNETTE' },
        { text: 'REVENDEUR', value: 'REVENDEUR' },
        { text: 'APPROVISIONNEMENT', value: 'APPROVISIONNEMENT' },
        { text: 'MOTO', value: 'MOTO' },
        { text: 'TRICYCLE', value: 'TRICYCLE' },
        { text: 'PARTICULIER', value: 'PARTICULIER' },
        { text: 'AUTRES', value: 'AUTRES' },
      ],
      exportValidationsLoading: false,
      
      // Headers de la table
      headers: [
        { text: this.$t("Utilisateur"), value: "user", sortable: false, width: "18%" },
        { text: this.$t("Type utilisateur"), value: "user_type", sortable: false, width: "10%" },
        { text: this.$t("Station d'origine"), value: "origin_station", sortable: false, width: "12%" },
        { text: this.$t("Immatriculation"), value: "plate_number", width: "12%" },
        { text: this.$t("Type"), value: "vehicle_type", width: "10%" },
        { text: this.$t("Statut"), value: "decision_status", width: "10%" },
        { text: this.$t("Défaut"), value: "is_default", align: "center", width: "6%" },
        { text: this.$t("Date création"), value: "created_at", width: "10%" },
        { text: this.$t("Actions"), value: "actions", sortable: false, align: "center", width: "12%" },
      ],
      
      // Dialogs
      dialog: { open: false, item: null },
      decision: { open: false, item: null, loadingApprove: false, loadingReject: false },
      confirm: { open: false, action: null, item: null, loading: false },
      changeType: { open: false, item: null, newType: null, loading: false },
      
      // Dialog de vérification de plaque
      plateWarning: {
        open: false,
        data: null,
        inputPlate: '',
      },
      
      // Statut de vérification de plaque
      plateVerificationStatus: null, // 'clear' | 'exists' | null
      
      // Observer et timeouts
      observer: null,
      searchTimeout: null,
      plateCheckTimeout: null,
    };
  },
  
  computed: {
    confirmMeta() {
      return {
        color: "amber darken-2",
        icon: "mdi-star-circle",
        title: this.$t("Définir ce véhicule par défaut ?"),
        subtitle: this.$t("Ce véhicule deviendra le véhicule principal de l'utilisateur."),
        cta: this.$t("Définir par défaut"),
      };
    },
    
    vehicleTypeOptions() {
      return Object.keys(VEHICLE_LABELS).map(key => ({
        value: key,
        label: VEHICLE_LABELS[key]
      }));
    },
  },
  
  mounted() {
    this.initInfiniteScroll();
    this.loadInitialData();
    this.loadStations();
  },
  
  beforeDestroy() {
    this.destroyObserver();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (this.plateCheckTimeout) clearTimeout(this.plateCheckTimeout);
  },
  
  methods: {
    toDatetime,
    
    // ========== UTILITAIRES DE FORMATAGE ==========
    
    capitalize(str) {
      if (!str) return '';
      return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    
    formatDate(datetime) {
      if (!datetime) return '-';
      const d = new Date(datetime);
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    
    formatTime(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime);
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    },
    
    vehicleTypeLabel(code) {
      return VEHICLE_LABELS[code] || code || "-";
    },
    
    getVehicleTypeColor(type) {
      const colors = {
        VTC: 'blue',
        TAXI_COMPTEUR: 'orange',
        TAXI_COMMUNAL: 'teal',
        MINI_CAR_URBAIN: 'purple',
        MINI_CAR_INTER_URBAIN: 'deep-purple',
        AUTO_CAR: 'indigo',
        POIDS_LOURDS: 'brown',
        CAMION: 'grey darken-1',
        CAMIONNETTE: 'blue-grey',
        REVENDEUR: 'pink',
        APPROVISIONNEMENT: 'cyan',
        APPROVISOINNEMENT: 'cyan',
        MOTO: 'red',
        TRICYCLE: 'lime darken-2',
        PARTICULIER: 'green',
        AUTRES: 'grey',
        FLOTTE_VTC: 'blue darken-2',
      };
      return colors[type] || 'grey';
    },
    
    getVehicleTypeIcon(type) {
      const icons = {
        VTC: 'mdi-car-side',
        TAXI_COMPTEUR: 'mdi-taxi',
        TAXI_COMMUNAL: 'mdi-taxi',
        MINI_CAR_URBAIN: 'mdi-van-passenger',
        MINI_CAR_INTER_URBAIN: 'mdi-van-utility',
        AUTO_CAR: 'mdi-bus',
        POIDS_LOURDS: 'mdi-truck',
        CAMION: 'mdi-truck-flatbed',
        CAMIONNETTE: 'mdi-truck-delivery',
        REVENDEUR: 'mdi-store',
        APPROVISIONNEMENT: 'mdi-package-variant',
        APPROVISOINNEMENT: 'mdi-package-variant',
        MOTO: 'mdi-motorbike',
        TRICYCLE: 'mdi-bicycle',
        PARTICULIER: 'mdi-car',
        AUTRES: 'mdi-help-circle',
        FLOTTE_VTC: 'mdi-car-multiple',
      };
      return icons[type] || 'mdi-car';
    },
    
    statusColor(st) {
      if (st === true)  return 'success';
      if (st === false) return 'error';
      return 'secondary';
    },
    
    statusLabel(st) {
      if (st === true)  return this.$t('Validé');
      if (st === false) return this.$t('Refusé');
      return this.$t('À statuer');
    },
    
    statusIcon(st) {
      if (st === true)  return 'mdi-check-circle';
      if (st === false) return 'mdi-close-circle';
      return 'mdi-clock-alert';
    },
    
    userFullname(item) {
      const full = (
        item?.user_fullname ||
        `${item?.user_first_name || ""} ${item?.user_last_name || ""}`.trim() ||
        (item?.user_username ? `@${item.user_username}` : `#${item?.user}`)
      );
      return this.capitalize(full);
    },
    
    userPhone(item) {
      return item?.user_phone_number || item?.phone_number || item?.user_phone || this.$t("N/A");
    },
    
    userJoinedAt(item) {
      return item?.user_date_joined || item?.date_joined || item?.user_joined_at || null;
    },
    
    getUserInitials(item) {
      const name = this.userFullname(item);
      const parts = name.split(' ').filter(Boolean);
      if (parts.length === 0) return 'U';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    
    /**
     * Génère les initiales d'un nom
     */
    getInitials(fullname) {
      if (!fullname) return 'U';
      const parts = fullname.trim().split(' ').filter(Boolean);
      if (parts.length === 0) return 'U';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    
    // ========== VÉRIFICATION DE PLAQUE ==========
    
    /**
     * Vérifie la plaque pour la décision (sans ouvrir la modale automatiquement)
     * IMPORTANT: On passe le user_id pour exclure ses propres véhicules
     */
    async checkPlateExistsForDecision(plateNumber, userId) {
      if (!plateNumber || plateNumber.trim().length < 3) {
        return false;
      }
      
      this.plateCheckLoading = true;
      
      try {
        // Passer le user_id pour exclure les véhicules de cet utilisateur
        const result = await VehicleAdminService.checkPlateNumber(plateNumber, userId);
        
        if (result.exists) {
          // Stocker les données pour affichage
          this.plateWarning.data = result;
          this.plateWarning.inputPlate = plateNumber;
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Erreur lors de la vérification de la plaque:', error);
        return false;
      } finally {
        this.plateCheckLoading = false;
      }
    },
    
    /**
     * Affiche les détails de la plaque B2B existante
     */
    showPlateDetails() {
      if (this.plateWarning.data && this.plateWarning.data.exists) {
        this.plateWarning.open = true;
      }
    },
    
    /**
     * Ferme la modale d'alerte de plaque
     */
    closePlateWarning() {
      this.plateWarning.open = false;
      this.plateWarning.data = null;
      this.plateWarning.inputPlate = '';
    },
    
    // ========== INFINITE SCROLL ==========
    
    initInfiniteScroll() {
      this.$nextTick(() => {
        if (!this.$refs.loadMoreTrigger) return;
        
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && this.hasMore && !this.loadingMore && !this.loading) {
                this.loadMore();
              }
            });
          },
          {
            root: null,
            rootMargin: "200px",
            threshold: 0.1,
          }
        );
        
        this.observer.observe(this.$refs.loadMoreTrigger);
      });
    },
    
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    
    async loadInitialData() {
      this.loading = true;
      this.currentPage = 1;
      this.items = [];
      this.hasMore = true;

      try {
        const params = this.buildParams(1);
        const { items, count, stats } = await VehicleAdminService.list(params);

        this.items = items;
        this.totalCount = count;
        this.hasMore = items.length >= 15;

        // Mettre à jour les KPIs si le backend retourne des stats
        if (stats) {
          this.kpiStats = stats;
        }

        this.$nextTick(() => {
          this.destroyObserver();
          this.initInfiniteScroll();
        });
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      
      this.loadingMore = true;
      this.currentPage += 1;
      
      try {
        const params = this.buildParams(this.currentPage);
        const { items } = await VehicleAdminService.list(params);
        
        if (items.length === 0) {
          this.hasMore = false;
        } else {
          this.items.push(...items);
          this.hasMore = items.length >= 15;
        }
        
        this.$nextTick(() => {
          this.destroyObserver();
          this.initInfiniteScroll();
        });
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        });
        this.currentPage -= 1;
      } finally {
        this.loadingMore = false;
      }
    },
    
    buildParams(page) {
      const params = {
        page,
        itemsPerPage: 15,
        sortBy: ["created_at"],
        sortDesc: [true],
        search: this.search?.trim() || "",
        status: this.statusFilter || "pending",
      };

      if (this.startDate) params.start_date = this.startDate;
      if (this.endDate)   params.end_date   = this.endDate;
      if (this.userTypeFilter) params.user_type = this.userTypeFilter;
      if (this.vehicleTypeFilter) params.vehicle_type = this.vehicleTypeFilter;
      if (this.stationFilter) params.station = this.stationFilter;

      return params;
    },
    
    // ========== GESTION DES FILTRES ==========
    
    onSearchChange() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.resetAndReload();
      }, 500);
    },
    
    onFilterChange() {
      this.resetAndReload();
    },
    
    resetAndReload() {
      this.loadInitialData();
    },
    
    // ========== DIALOGS ==========
    
    openDialog(item) {
      this.dialog.item = item;
      this.dialog.open = true;
    },
    
    /**
     * Ouvre la modale de décision et vérifie automatiquement la plaque
     */
    async openDecision(item) {
      this.decision.item = item;
      this.decision.open = true;
      
      // Reset du statut
      this.plateVerificationStatus = null;
      
      // Vérifier automatiquement la plaque en excluant les véhicules de cet utilisateur
      const exists = await this.checkPlateExistsForDecision(
        item.plate_number, 
        item.user // ID de l'utilisateur
      );
      
      // Mettre à jour le statut
      this.plateVerificationStatus = exists ? 'exists' : 'clear';
    },
    
    openChangeType(item) {
      this.changeType.item = item;
      this.changeType.newType = item.vehicle_type;
      this.changeType.open = true;
    },
    
    async doChangeType() {
      if (!this.changeType.item || !this.changeType.newType) return;
      
      this.changeType.loading = true;
      
      try {
        await VehicleAdminService.updateVehicleType(this.changeType.item.id, {
          vehicle_type: this.changeType.newType
        });
        
        this.changeType.open = false;
        
        const index = this.items.findIndex(i => i.id === this.changeType.item.id);
        if (index !== -1) {
          this.$set(this.items[index], 'vehicle_type', this.changeType.newType);
        }
        
        this.$store.commit("app/message", { 
          msg: this.$t("Type de véhicule modifié avec succès."), 
          color: "success" 
        });
      } catch (e) {
        this.$store.commit("app/message", { 
          msg: this.$t("messages.error.operation_failed"), 
          color: "error" 
        });
      } finally {
        this.changeType.loading = false;
      }
    },
    
    async onApprove() {
      if (!this.decision.item) return;
      this.decision.loadingApprove = true;
      
      try {
        await VehicleAdminService.approve(this.decision.item.id);
        this.decision.open = false;
        
        const index = this.items.findIndex(i => i.id === this.decision.item.id);
        if (index !== -1) {
          this.$set(this.items[index], 'decision_status', true);
          this.$set(this.items[index], 'is_active', true);
        }
        
        this.$store.commit("app/message", { 
          msg: this.$t("Véhicule validé."), 
          color: "success" 
        });
      } catch (e) {
        this.$store.commit("app/message", { 
          msg: this.$t("messages.error.operation_failed"), 
          color: "error" 
        });
      } finally {
        this.decision.loadingApprove = false;
      }
    },
    
    async onReject() {
      if (!this.decision.item) return;
      this.decision.loadingReject = true;
      
      try {
        await VehicleAdminService.reject(this.decision.item.id);
        this.decision.open = false;
        
        const index = this.items.findIndex(i => i.id === this.decision.item.id);
        if (index !== -1) {
          this.$set(this.items[index], 'decision_status', false);
          this.$set(this.items[index], 'is_active', false);
        }
        
        this.$store.commit("app/message", { 
          msg: this.$t("Véhicule refusé."), 
          color: "success" 
        });
      } catch (e) {
        this.$store.commit("app/message", { 
          msg: this.$t("messages.error.operation_failed"), 
          color: "error" 
        });
      } finally {
        this.decision.loadingReject = false;
      }
    },
    
    askConfirm(action, item) {
      if (action !== "default") return;
      this.confirm = { open: true, action, item, loading: false };
    },
    
    async doConfirm() {
      if (!this.confirm.item) return;
      this.confirm.loading = true;
      
      try {
        await VehicleAdminService.setDefault(this.confirm.item.id);
        this.confirm.open = false;
        
        this.items.forEach(item => {
          if (item.user === this.confirm.item.user) {
            this.$set(item, 'is_default', item.id === this.confirm.item.id);
          }
        });
        
        this.$store.commit("app/message", { 
          msg: this.$t("messages.success.operation"), 
          color: "success" 
        });
      } catch (e) {
        this.$store.commit("app/message", { 
          msg: this.$t("messages.error.operation_failed"), 
          color: "error" 
        });
      } finally {
        this.confirm.loading = false;
      }
    },
    
    // ========== EXPORT ==========
    
    async exportFidelityUsers() {
      try {
        this.exportLoading = true;
        const { saveAs } = await import("file-saver");
        const blob = await VehicleAdminService.exportFidelityUsers();
        saveAs(blob, `utilisateurs_fidelite_${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.$store.commit("app/message", { msg: this.$t("Telecharge avec succes"), color: "success" });
      } catch (e) {
        this.$store.commit("app/message", { msg: this.$t("messages.error.operation_failed"), color: "error" });
      } finally {
        this.exportLoading = false;
      }
    },

    async loadStations() {
      try {
        this.stations = await ShopDetailService.getprincipalAll();
      } catch (e) {
        console.error("Erreur chargement stations", e);
      }
    },

    async exportVehicleValidations() {
      try {
        this.exportValidationsLoading = true;
        const { saveAs } = await import("file-saver");
        const params = {};
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.search?.trim()) params.search = this.search.trim();
        if (this.startDate) params.start_date = this.startDate;
        if (this.endDate) params.end_date = this.endDate;
        if (this.userTypeFilter) params.user_type = this.userTypeFilter;
        if (this.vehicleTypeFilter) params.vehicle_type = this.vehicleTypeFilter;
        const blob = await VehicleAdminService.exportVehicleValidations(params);
        saveAs(blob, `validations_vehicules_${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.$store.commit("app/message", { msg: this.$t("Telecharge avec succes"), color: "success" });
      } catch (e) {
        this.$store.commit("app/message", { msg: this.$t("messages.error.operation_failed"), color: "error" });
      } finally {
        this.exportValidationsLoading = false;
      }
    },

    formatNumber(value) {
      if (!value && value !== 0) return "0";
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
  },
};
</script>

<style scoped>
/* ===== Section stats KPI ===== */
.stats-section {
  margin-top: 10px;
  margin-bottom: 0px;
  padding: 5px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 16px;
}
.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  padding: 0 5px;
}
.stat-card-main {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 20px !important;
  padding: 16px 24px;
  color: white;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  position: relative;
  overflow: hidden;
}
.stat-card-main::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  pointer-events: none;
}
.stat-card-main:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 40px rgba(25, 118, 210, 0.4) !important;
}
.stat-card-main .stat-secondary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}
.stat-card-product {
  border-radius: 16px !important;
  padding: 16px 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
}
.stat-card-product:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
  border-color: #90caf9;
}
.stat-label-mobile {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  margin-bottom: 6px;
}
.stat-value-mobile {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
}
.stat-value-warning { color: #ff9800; }
.stat-value-success { color: #4caf50; }
.stat-value-error { color: #f44336; }
.stat-icon-mobile {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}
.stat-icon-warning { background: rgba(255, 152, 0, 0.12); }
.stat-icon-success { background: rgba(76, 175, 80, 0.12); }
.stat-icon-error { background: rgba(244, 67, 54, 0.12); }
@media (max-width: 960px) {
  .stats-container {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .stats-container {
    grid-template-columns: 1fr;
  }
  .stat-value-mobile {
    font-size: 24px;
  }
}
.opacity-80 {
  opacity: .8;
}
.capitalize {
  text-transform: capitalize;
}
.plate-number {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
}
.action-buttons .v-btn {
  margin: 0 2px;
}
.action-buttons .v-btn:hover {
  transform: scale(1.1);
  transition: transform 0.2s;
}
/* Animation au survol des lignes */
.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}
/* Amélioration des chips */
.v-chip {
  font-size: 0.75rem !important;
  height: 24px !important;
}
</style>